import {
    MarketDataBarItem,
    MarketDataRefData,
} from 'marketData/store/MarketDataState';
import {
    InterpolationContext,
    toValueWithInterpolation,
} from 'utils/interpolation';
import {
    FillsGraphDataOrderStat,
    OrderFill,
} from 'widgets/fillsGraph/fillsGraphData';
import { BucketedData } from 'widgets/fillsGraph/fillsGraphDataBuckets';
import { getBucketAtTime } from 'utils/timeseries/bucket';
import isNil from 'lodash/isNil';

export function enrichTimeSeriesWithExternalMarketData(
    data: OrderFill[],
    dapiPriceData: MarketDataBarItem[],
    dapiRefData: MarketDataRefData | undefined,
    dapiIndexPriceData: MarketDataBarItem[] | undefined,
    dapiIndexRefData: MarketDataRefData | undefined,
    orderStatData: FillsGraphDataOrderStat | undefined
) {
    if (dapiPriceData.length === 0 || !dapiRefData) {
        return;
    }

    const interpolationContext = new InterpolationContext<OrderFill>(
        'backward'
    );

    const pnc = dapiRefData?.pnc;
    const indexPnc = dapiIndexRefData?.pnc;

    const indexValueRatio = computeIndexValueRatio(
        orderStatData,
        dapiRefData?.startTradingHoursTimestamp,
        indexPnc,
        dapiPriceData,
        dapiIndexPriceData
    );

    let lastWithPrice = -1;

    const findDapiItemAtTime = (
        time: number,
        dapiData: MarketDataBarItem[] | undefined
    ) =>
        dapiData &&
        dapiData.find((d) => d.time >= time && d.time < time + 60000);

    for (let i = data.length - 1; i >= 0; i--) {
        const item = data[i];
        const dapiItem = findDapiItemAtTime(item.time, dapiPriceData);
        const dapiIndexItem = findDapiItemAtTime(item.time, dapiIndexPriceData);

        if (dapiItem && dapiItem.close) {
            item.price = toValueWithInterpolation(dapiItem.close);
            if (lastWithPrice === -1) {
                lastWithPrice = i;
            }
        }

        const pricePctChange =
            item.price && pnc
                ? ((item.price.value - pnc) * 100) / pnc
                : undefined;

        if (indexPnc && dapiIndexItem?.close) {
            const indexPctChange =
                ((dapiIndexItem.close - indexPnc) * 100) / indexPnc;
            item.relativePerfIndex = toValueWithInterpolation(indexPctChange);

            if (pricePctChange) {
                item.relativePerfOrderToIndex = toValueWithInterpolation(
                    pricePctChange - indexPctChange
                );
            }
        }

        if (indexValueRatio && dapiIndexItem?.close) {
            item.normalisedBenchmarkPrice = toValueWithInterpolation(
                indexValueRatio * dapiIndexItem.close
            );
        }

        if (lastWithPrice > -1) {
            interpolationContext.interpolateStep(
                data,
                [
                    'price',
                    'relativePerfIndex',
                    'relativePerfOrderToIndex',
                    'normalisedBenchmarkPrice',
                ],
                i,
                lastWithPrice
            );
        }
    }

    function computeIndexValueRatio(
        orderStatData: FillsGraphDataOrderStat | undefined,
        startTradingHoursTimestamp: number | undefined,
        indexPnc: number | undefined,
        dapiPriceData: MarketDataBarItem[] | undefined,
        dapiIndexPriceData: MarketDataBarItem[] | undefined
    ): number | undefined {
        if (!indexPnc || !dapiPriceData || !dapiIndexPriceData) {
            return;
        }

        const arrivalTimestamp = orderStatData?.arrivalTimestamp;

        if (
            isNil(arrivalTimestamp) ||
            isNil(startTradingHoursTimestamp) ||
            (arrivalTimestamp ?? 0) < (startTradingHoursTimestamp ?? 0)
        ) {
            return pnc / indexPnc;
        }

        const endOfBucket = (start: number) => start + 60000;

        const getAtArrivalTime = (priceData: MarketDataBarItem[]) => {
            return priceData.find(
                (d) =>
                    d.time <= arrivalTimestamp &&
                    endOfBucket(d.time) > arrivalTimestamp
            )?.close;
        };

        const arrival = getAtArrivalTime(dapiPriceData);
        const indexValueAtArrival = getAtArrivalTime(dapiIndexPriceData);

        if (isNil(arrival) || !indexValueAtArrival) {
            return;
        }

        return arrival / indexValueAtArrival;
    }
}

export function getAverageVolumeForBuckets(
    dapiRefData: MarketDataRefData | undefined
) {
    if (!dapiRefData) {
        return;
    }

    const { averageVolume } = dapiRefData;

    return averageVolume;
}

export function volumesArePercentages(
    dapiBarData: MarketDataBarItem[] | undefined,
    dapiRefData: MarketDataRefData | undefined
) {
    return (
        (dapiBarData?.length ?? 0) > 0 &&
        !!getAverageVolumeForBuckets(dapiRefData)
    );
}

export function enrichBucketsWithExternalMarketData(
    bucketedData: BucketedData[],
    dapiPriceData: MarketDataBarItem[],
    dapiRefData: MarketDataRefData | undefined
) {
    const averageVolume = getAverageVolumeForBuckets(dapiRefData);

    if (!averageVolume) {
        return;
    }

    for (const minuteBucket of dapiPriceData) {
        const item = getBucketAtTime(bucketedData, minuteBucket.time, false);
        if (item) {
            item.actualVolume = item.actualVolume + (minuteBucket.volume ?? 0);
        }
    }

    const totalEstimated = bucketedData
        .map((d) => d.expectedVolume)
        .reduce((total, value) => total + value, 0);

    for (const bucket of bucketedData) {
        if (totalEstimated !== 0) {
            bucket.expectedVolume =
                (bucket.expectedVolume / totalEstimated) * 100;
        }
        bucket.actualVolume = (bucket.actualVolume / averageVolume) * 100;
    }
}
